// Thin wrapper around @capacitor-community/admob.
// Web/iOS → all functions are no-ops (returns safe defaults).
// Android native → real ads via the plugin.
//
// Ad unit IDs are read from Vite env at build time so we can ship test ads
// in dev and real ads in production without code changes:
//   VITE_ADMOB_REWARDED_ID
//   VITE_ADMOB_INTERSTITIAL_ID
// Falls back to Google's official test IDs when missing.

import { isAndroidApp } from "./platform";
import { isAdminTestAds } from "./admin-test-ads";

const TEST_REWARDED = "ca-app-pub-3940256099942544/5224354917";
const TEST_INTERSTITIAL = "ca-app-pub-3940256099942544/1033173712";
const TEST_BANNER = "ca-app-pub-3940256099942544/6300978111";

// Resolved at call time so the admin "Test Ads" toggle takes effect without
// a rebuild — flipping it forces Google's official test unit IDs.
const rewardedId = () =>
  isAdminTestAds() ? TEST_REWARDED : (import.meta.env.VITE_ADMOB_REWARDED_ID || TEST_REWARDED);
const interstitialId = () =>
  isAdminTestAds() ? TEST_INTERSTITIAL : (import.meta.env.VITE_ADMOB_INTERSTITIAL_ID || TEST_INTERSTITIAL);
const bannerId = () =>
  isAdminTestAds() ? TEST_BANNER : (import.meta.env.VITE_ADMOB_BANNER_ID || TEST_BANNER);

let initialized = false;

/**
 * Initialize AdMob SDK. Call once on app boot.
 * No-op on web / iOS for now.
 */
export async function initializeAds(): Promise<void> {
  if (!isAndroidApp() || initialized) return;
  try {
    const { AdMob } = await import("@capacitor-community/admob");
    await AdMob.initialize({
      initializeForTesting: import.meta.env.MODE !== "production" || isAdminTestAds(),
    });
    initialized = true;
  } catch (err) {
    // AdMob plugin not installed in the native shell yet — safe to ignore on web.
    console.warn("[ads] init skipped:", err);
  }
}

export interface RewardedResult {
  rewarded: boolean;
  /** Server-Side-Verification token. Send to grant-rewarded-credit edge fn. */
  ssvSignature?: string;
  /** SSV custom data used to bind the reward to a user id. */
  customData?: string;
  adUnitId: string;
}

/**
 * Show a rewarded ad. User must have tapped a button to call this — Google
 * policy forbids auto-played rewarded ads.
 *
 * Returns { rewarded: true } only if the user finished the full video.
 * On web returns { rewarded: false } immediately.
 */
export async function showRewardedAd(opts?: {
  userId?: string;
}): Promise<RewardedResult> {
  if (!isAndroidApp()) {
    return { rewarded: false, adUnitId: rewardedId() };
  }
  try {
    const { AdMob } = await import("@capacitor-community/admob");

    await AdMob.prepareRewardVideoAd({
      adId: rewardedId(),
      ssv: opts?.userId
        ? { userId: opts.userId, customData: opts.userId }
        : undefined,
    });
    const reward = await AdMob.showRewardVideoAd();

    return {
      rewarded: !!reward,
      // Plugin doesn't surface SSV signature client-side (verified server→server
      // by Google → our edge function via SSV callback URL configured in AdMob).
      // We forward customData so the edge fn can correlate to the user.
      customData: opts?.userId,
      adUnitId: rewardedId(),
    };
  } catch (err) {
    console.warn("[ads] rewarded failed:", err);
    return { rewarded: false, adUnitId: rewardedId() };
  }
}

/**
 * Show an interstitial ad. Fire-and-forget. Caller is responsible for
 * frequency capping (every Nth event).
 */
export async function showInterstitial(): Promise<void> {
  if (!isAndroidApp()) return;
  try {
    const { AdMob } = await import("@capacitor-community/admob");
    await AdMob.prepareInterstitial({ adId: interstitialId() });
    await AdMob.showInterstitial();
  } catch (err) {
    console.warn("[ads] interstitial failed:", err);
  }
}

// ─────────────────────────────────────────────────────────────────────
// Frequency capping helper for interstitials (client-side only).
// ─────────────────────────────────────────────────────────────────────
const INTERSTITIAL_KEY = "fitaura.interstitial.count";
const INTERSTITIAL_FREQUENCY = 2; // every 2nd generation

/**
 * Increment counter and return true if an interstitial should be shown
 * for this generation. Resets daily.
 */
export function shouldShowInterstitial(): boolean {
  // Admin Test Ads → always fire so every trigger can be verified.
  if (isAdminTestAds()) return true;
  try {
    const today = new Date().toISOString().slice(0, 10);
    const raw = localStorage.getItem(INTERSTITIAL_KEY);
    let parsed: { date: string; count: number } = { date: today, count: 0 };
    if (raw) {
      try {
        parsed = JSON.parse(raw);
      } catch {
        // ignore
      }
    }
    if (parsed.date !== today) parsed = { date: today, count: 0 };
    parsed.count += 1;
    localStorage.setItem(INTERSTITIAL_KEY, JSON.stringify(parsed));
    return parsed.count % INTERSTITIAL_FREQUENCY === 0;
  } catch {
    return false;
  }
}

// ─────────────────────────────────────────────────────────────────────
// Banner ads (bottom of screen, adaptive size). Free Android users only.
// ─────────────────────────────────────────────────────────────────────
let bannerShown = false;

export async function showBanner(): Promise<void> {
  if (!isAndroidApp() || bannerShown) return;
  try {
    const { AdMob, BannerAdPosition, BannerAdSize } = await import("@capacitor-community/admob");
    await AdMob.showBanner({
      adId: bannerId(),
      adSize: BannerAdSize.ADAPTIVE_BANNER,
      position: BannerAdPosition.BOTTOM_CENTER,
      margin: 72, // sit above the 56dp bottom nav + safe-area inset
      isTesting: import.meta.env.MODE !== "production" || isAdminTestAds(),
    });
    bannerShown = true;
  } catch (err) {
    console.warn("[ads] banner failed:", err);
  }
}

export async function hideBanner(): Promise<void> {
  if (!isAndroidApp() || !bannerShown) return;
  try {
    const { AdMob } = await import("@capacitor-community/admob");
    await AdMob.removeBanner();
    bannerShown = false;
  } catch (err) {
    console.warn("[ads] banner hide failed:", err);
  }
}

// ─────────────────────────────────────────────────────────────────────
// Stable device id for per-device rewarded-ad cap. Cached in localStorage
// so re-installs keep the same id when possible (Capacitor Device returns
// an app-install-scoped uuid on Android).
// ─────────────────────────────────────────────────────────────────────
const DEVICE_ID_KEY = "fitaura.device.id";

export async function getStableDeviceId(): Promise<string | null> {
  if (!isAndroidApp()) return null;
  try {
    const cached = localStorage.getItem(DEVICE_ID_KEY);
    if (cached) return cached;
    const { Device } = await import("@capacitor/device");
    const info = await Device.getId();
    const id = (info as { identifier?: string; uuid?: string }).identifier
      ?? (info as { uuid?: string }).uuid
      ?? null;
    if (id) localStorage.setItem(DEVICE_ID_KEY, id);
    return id;
  } catch (err) {
    console.warn("[ads] getStableDeviceId failed:", err);
    return null;
  }
}
